// 锚定按月计费（App Store 式）的周期工具，与 PandaUnion
// ts/services/billing_period.ts 保持同一套语义：
// 周期 = [period_start, period_end)，anchor_day 为购买日（1-31，短月截断）。
// 服务行带 billing_mode === 'anchor' 且 period_end 非空时走锚定逻辑，
// 否则回落到旧的自然月逻辑（由调用方保留）。
import type { Dayjs } from 'dayjs'

export const BILLING_TZ = 'Asia/Shanghai'

export interface AnchorBilledLike {
  billing_mode?: string | null
  period_start?: string | Date | null
  period_end?: string | Date | null
  anchor_day?: number | null
  extra?: { buy_length?: number } | null
}

export interface AnchorBillingPeriodRange {
  startTime: Date
  endTime: Date
}

const anchorBoundaryMs = (value: string | Date | null | undefined): number => {
  if (value == null || (typeof value === 'string' && value.trim().length === 0)) return Number.NaN
  return new Date(value).getTime()
}

export const isAnchorBilledService = (service?: AnchorBilledLike | null): boolean =>
  Boolean(service && service.billing_mode === 'anchor' && service.period_end != null)

export const currentAnchorBillingPeriodRange = (
  service: AnchorBilledLike,
  nowInput: number | Date = Date.now(),
): AnchorBillingPeriodRange => {
  const nowMs = nowInput instanceof Date ? nowInput.getTime() : Number(nowInput)
  if (!Number.isFinite(nowMs)) {
    throw new Error('Invalid current time for anchor billing period')
  }

  const startMs = anchorBoundaryMs(service.period_start)
  const endMs = anchorBoundaryMs(service.period_end)
  if (service.billing_mode !== 'anchor' || !Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs || nowMs < startMs) {
    throw new Error('Invalid anchor billing period')
  }
  return {
    startTime: new Date(startMs),
    endTime: new Date(nowMs < endMs ? nowMs : endMs - 1),
  }
}

// 从某个周期边界出发前进 n 个锚定月（先归一到月初再加月，避免短月截断漂移）
export const addAnchoredMonths = (dayjs: any, boundary: string | Date, n: number, anchorDay: number): Dayjs => {
  const base = dayjs(boundary).tz(BILLING_TZ)
  const target = base.date(1).add(n, 'month')
  return target.date(Math.min(anchorDay, target.daysInMonth()))
}

// 当前周期剩余比例（升级差价折算），钳到 [0, 1]
export const anchoredPeriodRemainingFraction = (service: AnchorBilledLike, nowMs = Date.now()): number => {
  const start = +new Date(service.period_start as any)
  const end = +new Date(service.period_end as any)
  if (!(end > start)) return 0
  return Math.min(1, Math.max(0, (end - nowMs) / (end - start)))
}

// 服务到期日（= 当前周期终点 + 剩余预付月数），返回 Dayjs（Asia/Shanghai）
export const anchoredServiceExpiry = (dayjs: any, service: AnchorBilledLike): Dayjs => {
  const buyLength = Number(service.extra?.buy_length) || 1
  const anchorDay = service.anchor_day || dayjs(service.period_end).tz(BILLING_TZ).date()
  if (buyLength <= 1) return dayjs(service.period_end).tz(BILLING_TZ)
  return addAnchoredMonths(dayjs, service.period_end as any, buyLength - 1, anchorDay)
}

// 到期日显示格式：锚定计费显示真实日期（不再是「YYYY/MM/01」）
export const ANCHOR_DUE_FORMAT = 'YYYY/MM/DD'
